import { useMemo } from 'react';
import { IoClose } from 'react-icons/io5';
import { MdOutlineAccessTime } from 'react-icons/md';
import { Modal, ModalBody, ModalContent, ModalHeader, Spinner, Tooltip } from '@ds';
import { TmdxResourceCard } from '@features/components/ResourceCards';
import { getCurrentStatusTill } from '@lp/features/RoutePlanner/utils';
import { OrganSpecializationsList } from '@lp/features/RoutePlanner/components/OrganSpecializationsList/OrganSpecializationsList';
import SectionRow from '@features/components/CaseOverview/components/SectionRow/SectionRow';
import { CertificateOrgans } from '@lp/features/AssignmentTables/ClinicalSpecialistsTable/CertificateOrgans';
import { CaseClinicalSpecialistResource, CaseResource, ResourceLogisticsStatus } from '@/api/apis/case';
import { EmployeeComposed } from '@/api/apis/resources';
import {
  CS_CERTIFICATE_TYPE,
  CS_PROCTOR_SHADOW_TITLE,
  displayPhoneNumber,
  getLastTrackedLocation,
  getLastTrackedTime,
} from '@/utils/helpers';
import { employeeLabelColors, RESOURCE_STATUS_TEXT_MAP } from '@/constants/resources.constants';
import { ViewResourceModalFooter } from './ViewResourceModalFooter';

const REMOVE_ALLOWED_STATUSES = [
  ResourceLogisticsStatus.Reserved,
  ResourceLogisticsStatus.BookingSent,
  ResourceLogisticsStatus.Booked,
];

export type ViewClinicalSpecialistModalProps = {
  title: string;
  resource?: CaseResource<CaseClinicalSpecialistResource> | null;
  isOpen: boolean;
  isLoading: boolean;
  isAssignEnabled: boolean;
  isRemoveEnabled: boolean;
  size?: 'md' | 'xs' | 'sm' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | 'full';
  onRemove: () => void;
  onAssign: () => void;
  onClose: () => void;
};

export const ViewClinicalSpecialistModal = ({
  title,
  resource,
  isOpen,
  isLoading,
  isAssignEnabled,
  isRemoveEnabled,
  size = 'lg',
  onRemove,
  onAssign,
  onClose,
}: ViewClinicalSpecialistModalProps) => {
  const lastCall = resource?.data ? `${resource.data.currentDeploymentData.lastCallDaysAgo}d` : 'N/A';
  const currentEndTimeString = useMemo(
    () =>
      getCurrentStatusTill(
        resource!.data!.csShiftAvailabilityData?.currentShift || null,
        (resource!.data! as EmployeeComposed).baseLocation!.location.timezone,
      ),
    [resource],
  );

  return (
    <Modal
      size={size}
      isOpen={isOpen}
      onClose={onClose}
      backdrop="opaque"
      closeButton={<IoClose size={45} style={{ top: 15 }} />}
      classNames={{
        base: 'px-6 bg-base-80',
        body: 'border-b-1 border-base-30 px-0 py-6 text-l-r',
        header: 'px-0 font-secondary text-hl! text-base-20',
        footer: 'px-0 flex gap-6',
        wrapper: 'z-420',
        backdrop: 'z-419',
      }}
    >
      <ModalContent>
        {() => (
          <>
            {isLoading && (
              <div className="absolute top-0 left-0 flex justify-center items-center z-50 w-full h-full bg-white bg-opacity-40">
                <Spinner size="md" />
              </div>
            )}
            <ModalHeader>{title}</ModalHeader>
            <ModalBody>
              <TmdxResourceCard
                className="bg-base-90 px-4 py-3"
                label="Actions"
                list={[]}
                type="user"
                title={`${(resource!.data! as EmployeeComposed).firstName} ${(resource!.data! as EmployeeComposed).lastName}`}
                subTitle="Clinical Specialist"
                phoneNumber={displayPhoneNumber((resource!.data! as EmployeeComposed).phone)}
                avatarProps={{
                  src: (resource!.data! as EmployeeComposed).profilePhoto,
                  label: resource!.resourceData?.labels?.[0] || undefined,
                  isStatus: !!resource!.resourceData?.labels?.[0],
                  chipProps: {
                    color: resource!.resourceData?.labels?.[0]
                      ? employeeLabelColors[resource!.resourceData?.labels?.[0]]
                      : undefined,
                  },
                }}
                chipProps={{
                  status: resource!.logisticsStatus,
                }}
                chipText={RESOURCE_STATUS_TEXT_MAP[resource!.logisticsStatus]}
                isReadOnly
                menuDisabled
              />
              <div className="flex flex-col gap-4 p-6 bg-base-80 rounded-xl shadow-s">
                <SectionRow label="Base location">
                  {(resource!.data! as EmployeeComposed).baseLocation!.name}
                </SectionRow>
                <SectionRow label="Organ">
                  <OrganSpecializationsList
                    organs={(resource!.data! as EmployeeComposed).organSpecializations}
                    size="lg"
                    bgTone="bg60"
                  />
                </SectionRow>
                <SectionRow
                  label={`${CS_PROCTOR_SHADOW_TITLE[CS_CERTIFICATE_TYPE.PROCTOR]} / ${CS_PROCTOR_SHADOW_TITLE[CS_CERTIFICATE_TYPE.SHADOW]}`}
                >
                  <div className="flex gap-1 items-center">
                    <CertificateOrgans
                      type={CS_CERTIFICATE_TYPE.PROCTOR}
                      organs={resource?.data?.proctorOrganSpecializations}
                    />
                    <CertificateOrgans
                      type={CS_CERTIFICATE_TYPE.SHADOW}
                      organs={resource?.data?.shadowOrganSpecializations}
                    />
                  </div>
                </SectionRow>
                <SectionRow label="Current location">
                  <div className="flex items-center gap-2">
                    {!!resource?.data?.trackedLocation && (
                      <Tooltip content={getLastTrackedTime(resource.data.trackedLocation)}>
                        <div>
                          <MdOutlineAccessTime className="text-base-30" size={20} />
                        </div>
                      </Tooltip>
                    )}
                    <div className="truncate">{getLastTrackedLocation(resource?.data?.trackedLocation)}</div>
                  </div>
                </SectionRow>
                <SectionRow label="Last call">{lastCall}</SectionRow>
                <SectionRow truncate={false} label="On shift till">
                  {currentEndTimeString || 'N/A'}
                </SectionRow>
              </div>
            </ModalBody>
            <ViewResourceModalFooter
              resource={resource}
              isLoading={isLoading}
              isAssignEnabled={isAssignEnabled}
              isRemoveEnabled={isRemoveEnabled}
              removeAllowedStatuses={REMOVE_ALLOWED_STATUSES}
              onAssign={onAssign}
              onRemove={onRemove}
            />
          </>
        )}
      </ModalContent>
    </Modal>
  );
};
